// Results tab of the student profile + the printable report card, which the
// Exams screen also opens for any student in its marks grid.
import { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { getStudentResults, getMySchool } from '../../api/client';
import Button from '../../ui/Button';
import { Card, CardHeader } from '../../ui/Card';
import PrintSheet, { PrintHeader } from '../../ui/PrintSheet';
import { fmtDate, resultFor, pct, BOARD_LABELS } from '../../ui/format';

const examTotals = (exam) => {
  const obtained = exam.subjects.reduce((s, x) => s + Number(x.marks_obtained), 0);
  const max = exam.subjects.reduce((s, x) => s + Number(x.max_marks), 0);
  return { obtained, max, pct: pct(obtained, max) };
};

export default function ResultsTab({ student, schoolName, board }) {
  const [exams, setExams] = useState(null);
  const [msg,   setMsg]   = useState('');
  const [print, setPrint] = useState(false); // false | 'standard' | 'traditional'

  useEffect(() => {
    getStudentResults(student.id)
      .then(setExams)
      .catch(err => setMsg('Failed to load results: ' + (err.response?.data?.error || err.message)));
  }, [student.id]);

  if (msg) return <p className="text-critical text-sm font-bold">{msg}</p>;
  if (!exams) return <p className="text-ink-soft text-sm">Loading…</p>;
  if (!exams.length) {
    return <p className="text-ink-soft text-sm">No exam results yet. Teachers can enter marks in the staff app, or you can use Exams &amp; Results.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end gap-2">
        <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" onClick={() => setPrint('standard')}>
          <Printer size={13} /> Print report card
        </Button>
        <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" onClick={() => setPrint('traditional')} title="RBSE-style periodic report card (First/Second/Third/Half Yearly/Annual, Written-Oral split, co-scholastic grades)">
          <Printer size={13} /> Print RBSE format
        </Button>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
        {exams.map(exam => {
          const t = examTotals(exam);
          return (
            <Card key={exam.exam_id}>
              <CardHeader title={exam.name}
                action={<span className="text-xs font-bold text-ink-soft">{fmtDate(exam.exam_date)}</span>} />
              <table className="w-full text-sm">
                <tbody>
                  {exam.subjects.map(s => (
                    <tr key={s.subject} className="border-t border-line-soft first:border-t-0">
                      <td className="px-5 py-2 text-ink">{s.subject}</td>
                      <td className="px-5 py-2 text-right tabular-nums font-semibold">{Number(s.marks_obtained)} / {Number(s.max_marks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 border-t border-line flex items-center justify-between bg-paper rounded-b-m">
                <span className="text-sm font-bold text-ink tabular-nums">{t.obtained} / {t.max} · {t.pct}%</span>
                <span className="text-sm font-extrabold font-display text-transit-deep">
                  {!board || board === 'CBSE' ? 'Grade ' : ''}{resultFor(t.pct, board)}
                  {exam.rank ? <span className="text-ink-soft font-bold text-xs"> · Rank {exam.rank}/{exam.class_size}</span> : null}
                </span>
              </div>
            </Card>
          );
        })}
      </div>
      {print === 'standard' && <ReportCardSheet student={student} exams={exams} schoolName={schoolName} board={board} onClose={() => setPrint(false)} />}
      {print === 'traditional' && <TraditionalReportCardSheet student={student} exams={exams} schoolName={schoolName} board={board} onClose={() => setPrint(false)} />}
    </div>
  );
}

// One printed report card covering every exam the student has results for.
// `student` needs name, class_name, roll_number, admission_no, father_name/mother_name/guardian_name where known.
export function ReportCardSheet({ student, exams, schoolName, board, onClose }) {
  const subjects = [...new Set(exams.flatMap(e => e.subjects.map(s => s.subject)))].sort();
  const ordered = exams.slice().reverse(); // oldest exam first, left to right
  const cell = (exam, subject) => exam.subjects.find(s => s.subject === subject);
  const isCbse = !board || board === 'CBSE';
  const resultLabel = isCbse ? 'Grade' : 'Result';

  return (
    <PrintSheet title={`Report card — ${student.name}`} onClose={onClose}>
      <PrintHeader schoolName={schoolName}
        docTitle={`Progress Report Card${board ? ` · ${BOARD_LABELS[board] || board}` : ''}`}
        right={<>Printed {fmtDate(new Date().toISOString())}</>} />
      <div className="flex gap-5 items-start mb-4">
      {student.photo && <img src={student.photo} alt="" className="w-[70px] h-[84px] object-cover rounded-s border border-[#c9d3d0]" />}
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[13.5px] flex-1">
        <span>Student: <b>{student.name}</b></span>
        <span>Class: <b>{student.class_name || '—'}</b>{student.roll_number ? <> · Roll <b>{student.roll_number}</b></> : null}</span>
        <span>Admission No.: <b>{student.admission_no || '—'}</b></span>
        <span>Father / Guardian: <b>{student.father_name || student.guardian_name || '—'}</b></span>
        {student.mother_name && <span>Mother: <b>{student.mother_name}</b></span>}
      </div>
      </div>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="text-left">Subject</th>
            {ordered.map(e => <th key={e.exam_id} className="text-center">{e.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {subjects.map(sub => (
            <tr key={sub}>
              <td>{sub}</td>
              {ordered.map(e => {
                const c = cell(e, sub);
                return <td key={e.exam_id} className="text-center">{c ? `${Number(c.marks_obtained)}/${Number(c.max_marks)}` : '—'}</td>;
              })}
            </tr>
          ))}
          <tr>
            <td className="font-bold">Total</td>
            {ordered.map(e => { const t = examTotals(e); return <td key={e.exam_id} className="text-center font-bold">{t.obtained}/{t.max}</td>; })}
          </tr>
          <tr>
            <td className="font-bold">Percentage</td>
            {ordered.map(e => <td key={e.exam_id} className="text-center font-bold">{examTotals(e).pct}%</td>)}
          </tr>
          <tr>
            <td className="font-bold">{resultLabel}</td>
            {ordered.map(e => <td key={e.exam_id} className="text-center font-bold">{resultFor(examTotals(e).pct, board)}</td>)}
          </tr>
          {ordered.some(e => e.rank) && (
            <tr>
              <td className="font-bold">Class rank</td>
              {ordered.map(e => <td key={e.exam_id} className="text-center font-bold">{e.rank ? `${e.rank} / ${e.class_size}` : '—'}</td>)}
            </tr>
          )}
        </tbody>
      </table>
      <p className="text-[11.5px] text-[#405058] mt-3">
        {isCbse
          ? 'Grades: A1 91–100 · A2 81–90 · B1 71–80 · B2 61–70 · C1 51–60 · C2 41–50 · D 33–40 · E below 33'
          : 'Result: Distinction 75% and above · First Division 60–74% · Second Division 45–59% · Third Division 33–44% · Fail below 33%'}
      </p>
      <div className="flex justify-between mt-14 text-xs text-[#405058]">
        <div className="border-t border-[#101b22] pt-1 px-6">Class teacher</div>
        <div className="border-t border-[#101b22] pt-1 px-6">Principal</div>
        <div className="border-t border-[#101b22] pt-1 px-6">Parent</div>
      </div>
    </PrintSheet>
  );
}

// RBSE-style periodic report card — subjects scored across five fixed slots
// (First/Second/Third periodic tests, Half Yearly, Annual), each split into
// Written/Oral/Total, with a running "upto Half Yearly" subtotal and a final
// Overall column — matching the printed format Rajasthan-board schools use.
// Slots are matched to this student's actual exams by name (the exam name
// must contain "first"/"1st", "second"/"2nd", "third"/"3rd", "half", or
// "annual"/"final" — case-insensitive); a slot with no matching exam still
// prints, just blank, so the sheet's shape never changes. Built from the same
// `exams` payload as ReportCardSheet above (GET /exam-results/student/:id).
export const REPORT_SLOTS = [
  { key: 'first',  label: 'First',           test: n => /\bfirst\b|\b1st\b/i.test(n) },
  { key: 'second', label: 'Second',          test: n => /\bsecond\b|\b2nd\b/i.test(n) },
  { key: 'third',  label: 'Third',           test: n => /\bthird\b|\b3rd\b/i.test(n) },
  { key: 'half',   label: 'Half Yrly Exam.', test: n => /half/i.test(n) },
  { key: 'annual', label: 'Annual Exam',     test: n => /annual|\bfinal\b/i.test(n) },
];

// Activity Based Evaluation (RBSE's co-scholastic areas — Work Experience,
// Art Education, Physical Education) uses a plain 5-point grade, separate
// from the academic board scale above it on the same card.
function activityGrade(pct) {
  if (pct == null) return '—';
  if (pct >= 75) return 'A';
  if (pct >= 60) return 'B';
  if (pct >= 45) return 'C';
  if (pct >= 33) return 'D';
  return 'E';
}

export function TraditionalReportCardSheet({ student, exams, schoolName, board, onClose }) {
  const [letterhead, setLetterhead] = useState(null);
  useEffect(() => { getMySchool().then(setLetterhead).catch(() => setLetterhead({})); }, []);

  const isCbse = !board || board === 'CBSE';
  const resultLabel = isCbse ? 'Grade' : 'Result';

  const slots = REPORT_SLOTS.map(s => ({ ...s, exam: exams.find(e => s.test(e.name)) || null }));
  const annualExam = slots.find(s => s.key === 'annual')?.exam || null;
  const latestExam = exams[0] || null; // exams arrives most-recent-first

  const subjectNames = (category) => {
    const seen = new Set(); const names = [];
    for (const e of exams) for (const s of e.subjects) {
      if (s.category === category && !seen.has(s.subject)) { seen.add(s.subject); names.push(s.subject); }
    }
    return names.sort();
  };
  const academicSubjects = subjectNames('academic');
  const coScholasticSubjects = subjectNames('co_scholastic');

  const cellValue = (subject, exam, type) => {
    if (!exam) return null;
    const s = exam.subjects.find(x => x.subject === subject);
    if (!s) return null;
    if (type === 'total') return Number(s.marks_obtained);
    const c = s.components?.[type];
    return c != null ? Number(c.marks_obtained) : (exam.has_oral ? 0 : null);
  };
  const cellMax = (subject, exam, type) => {
    if (!exam) return null;
    const s = exam.subjects.find(x => x.subject === subject);
    if (!s) return null;
    if (type === 'total') return Number(s.max_marks);
    const c = s.components?.[type];
    return c != null ? Number(c.max_marks) : null;
  };
  const fmt = v => v == null ? '—' : v;

  // Sum of a subject's Total across First/Second/Third/Half Yearly only —
  // "TTL UPTO HYE" in the RBSE template.
  const uptoHye = (subject, getter) => {
    let sum = 0, any = false;
    for (const slot of slots) {
      if (slot.key === 'annual') continue;
      const v = getter(subject, slot.exam, 'total');
      if (v != null) { sum += v; any = true; }
    }
    return any ? sum : null;
  };
  const overall = (subject, getter) => {
    const upto = uptoHye(subject, getter);
    const ann = getter(subject, annualExam, 'total');
    return (upto == null && ann == null) ? null : (upto || 0) + (ann || 0);
  };

  const SubjectRow = ({ subject }) => {
    const obtained = overall(subject, cellValue);
    const max = overall(subject, cellMax);
    const pct = max > 0 && obtained != null ? Math.round((obtained / max) * 1000) / 10 : null;
    return (
      <tr key={subject}>
        <td className="text-left italic font-bold whitespace-nowrap">{subject}</td>
        {slots.map(slot => [
          <td key={slot.key + 'w'} className="text-center">{fmt(cellValue(subject, slot.exam, 'written'))}</td>,
          <td key={slot.key + 'o'} className="text-center">{fmt(cellValue(subject, slot.exam, 'oral'))}</td>,
          <td key={slot.key + 't'} className="text-center font-bold">{fmt(cellValue(subject, slot.exam, 'total'))}</td>,
        ])}
        <td className="text-center font-bold">{fmt(uptoHye(subject, cellValue))}</td>
        <td className="text-center font-bold">{fmt(obtained)}</td>
        <td className="text-center font-bold">{pct != null ? `${pct}%` : '—'}</td>
        <td className="text-center font-bold">{pct != null ? resultFor(pct, board) : '—'}</td>
      </tr>
    );
  };

  const academicMax = academicSubjects.length ? overall(academicSubjects[0], cellMax) : null;
  const academicObtained = academicSubjects.reduce((sum, s) => sum + (overall(s, cellValue) || 0), 0);
  const academicMaxTotal = academicSubjects.reduce((sum, s) => sum + (overall(s, cellMax) || 0), 0);
  const overallPct = academicMaxTotal > 0 ? Math.round((academicObtained / academicMaxTotal) * 1000) / 10 : null;
  const passed = overallPct != null && overallPct >= 33; // same pass floor as resultFor()'s Third Division / gradeFor()'s D

  // Activity Based Evaluation reuses whatever co-scholastic marks exist,
  // across up to five exams in chronological order (oldest first) — not
  // tied to the First/Second/Third/Half Yearly/Annual slots above, since
  // co-scholastic areas aren't always assessed on the same calendar.
  const activityExams = exams.slice().reverse().slice(-5);
  const activityLabels = ['First', 'Second', 'Third', 'Fourth', 'Fifth'].slice(0, activityExams.length);

  // Cumulative present/total attendance, one column per slot that has a
  // matching exam (studentExams() already returns each exam's attendance as
  // a running total from the start of the session), plus a grand total from
  // the most recent exam overall.
  const attendanceSlots = slots.filter(s => s.exam);

  return (
    <PrintSheet title={`Report card — ${student.name}`} onClose={onClose} landscape>
      <div className="flex items-start justify-between text-[11px] font-bold text-[#405058] mb-1">
        <span>{letterhead?.reco_no ? `Reco. No. ${letterhead.reco_no}` : ''}</span>
        <span>{letterhead?.reg_no ? `Reg. No. ${letterhead.reg_no}` : ''}</span>
      </div>
      <div className="flex items-center justify-between gap-4 border-b-2 border-[#101b22] pb-3 mb-4">
        {letterhead?.logo_data_url
          ? <img src={letterhead.logo_data_url} alt="" className="w-14 h-14 rounded-full object-cover border border-[#c9d3d0]" />
          : <div className="w-14 h-14" />}
        <div className="text-center flex-1">
          <div className="font-display font-extrabold text-xl">{schoolName}</div>
          <div className="text-[12px] font-bold uppercase tracking-wider text-[#405058] mt-0.5">
            Progress Report Card{board ? ` · ${BOARD_LABELS[board] || board}` : ''}
          </div>
        </div>
        {student.photo
          ? <img src={student.photo} alt="" className="w-14 h-[66px] object-cover border border-[#c9d3d0]" />
          : <div className="w-14 h-[66px]" />}
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[13px] mb-4">
        <span>Session: <b>{letterhead?.current_session || '—'}</b></span>
        <span>DISE Code: <b>{letterhead?.dise_code || '—'}</b></span>
        <span>Name of the Student: <b>{student.name}</b></span>
        <span>Date of Birth: <b>{fmtDate(student.date_of_birth)}</b></span>
        <span>Father's Name: <b>{student.father_name || student.guardian_name || '—'}</b></span>
        <span>S.R. No.: <b>{student.admission_no || '—'}</b></span>
        <span>Mother's Name: <b>{student.mother_name || '—'}</b></span>
        <span>Class &amp; Section: <b>{student.class_name || '—'}</b>{student.roll_number ? <> · Roll <b>{student.roll_number}</b></> : null}</span>
      </div>

      <div className="overflow-x-auto">
      <table className="w-full text-[10.5px] border-collapse border border-[#101b22]">
        <thead>
          <tr>
            <th rowSpan={2} className="border border-[#101b22] text-left px-1">Subject</th>
            {slots.map(slot => <th key={slot.key} colSpan={3} className="border border-[#101b22]">{slot.label}</th>)}
            <th rowSpan={2} className="border border-[#101b22]">Ttl Upto<br />HYE</th>
            <th colSpan={3} className="border border-[#101b22]">Overall</th>
          </tr>
          <tr>
            {slots.map(slot => [
              <th key={slot.key + 'w'} className="border border-[#101b22] font-normal">WRT</th>,
              <th key={slot.key + 'o'} className="border border-[#101b22] font-normal">ORAL</th>,
              <th key={slot.key + 't'} className="border border-[#101b22] font-normal">TTL</th>,
            ])}
            <th className="border border-[#101b22] font-normal">Total</th>
            <th className="border border-[#101b22] font-normal">%</th>
            <th className="border border-[#101b22] font-normal">{resultLabel}</th>
          </tr>
          <tr className="font-bold">
            <td className="border border-[#101b22] text-left px-1">Max Marks</td>
            {slots.map(slot => [
              <td key={slot.key + 'w'} className="border border-[#101b22] text-center">{fmt(academicSubjects.length ? cellMax(academicSubjects[0], slot.exam, 'written') : null)}</td>,
              <td key={slot.key + 'o'} className="border border-[#101b22] text-center">{fmt(academicSubjects.length ? cellMax(academicSubjects[0], slot.exam, 'oral') : null)}</td>,
              <td key={slot.key + 't'} className="border border-[#101b22] text-center">{fmt(academicSubjects.length ? cellMax(academicSubjects[0], slot.exam, 'total') : null)}</td>,
            ])}
            <td className="border border-[#101b22] text-center">{fmt(academicSubjects.length ? uptoHye(academicSubjects[0], cellMax) : null)}</td>
            <td className="border border-[#101b22] text-center" colSpan={3}>{fmt(academicMax)}</td>
          </tr>
        </thead>
        <tbody>
          {academicSubjects.map(subject => <SubjectRow key={subject} subject={subject} />)}
        </tbody>
      </table>
      </div>

      {coScholasticSubjects.length > 0 && (
        <div className="overflow-x-auto mt-3">
        <table className="w-full text-[10.5px] border-collapse border border-[#101b22]">
          <thead>
            <tr>
              <th className="border border-[#101b22] text-left px-1">Activity Based Evaluation</th>
              {activityLabels.map(l => <th key={l} className="border border-[#101b22]">{l}</th>)}
              <th colSpan={3} className="border border-[#101b22]">Overall</th>
            </tr>
            <tr className="font-bold">
              <td className="border border-[#101b22] text-left px-1">Max Marks</td>
              {activityExams.map((e, i) => {
                const anySubj = coScholasticSubjects.find(s => e.subjects.find(x => x.subject === s));
                const s = anySubj && e.subjects.find(x => x.subject === anySubj);
                return <td key={i} className="border border-[#101b22] text-center">{s ? Number(s.max_marks) : '—'}</td>;
              })}
              <td className="border border-[#101b22] text-center" colSpan={3}>Max / % / Grade</td>
            </tr>
          </thead>
          <tbody>
            {coScholasticSubjects.map(subject => {
              let obtained = 0, max = 0;
              for (const e of activityExams) {
                const s = e.subjects.find(x => x.subject === subject);
                if (s) { obtained += Number(s.marks_obtained); max += Number(s.max_marks); }
              }
              const pct = max > 0 ? Math.round((obtained / max) * 1000) / 10 : null;
              return (
                <tr key={subject}>
                  <td className="text-left italic font-bold">{subject}</td>
                  {activityExams.map(e => {
                    const s = e.subjects.find(x => x.subject === subject);
                    return <td key={e.exam_id} className="text-center">{s ? Number(s.marks_obtained) : '—'}</td>;
                  })}
                  <td className="text-center font-bold">{max || '—'}</td>
                  <td className="text-center font-bold">{pct != null ? `${pct}%` : '—'}</td>
                  <td className="text-center font-bold">{activityGrade(pct)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      <div className="overflow-x-auto mt-3">
      <table className="w-full text-[10.5px] border-collapse border border-[#101b22]">
        <thead>
          <tr>
            <th className="border border-[#101b22] text-left px-1">Attendance</th>
            {attendanceSlots.map(slot => <th key={slot.key} className="border border-[#101b22]">{slot.label}</th>)}
            <th className="border border-[#101b22]">Grand Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="text-left italic font-bold">Student Attendance Upto</td>
            {attendanceSlots.map(slot => <td key={slot.key} className="text-center">{slot.exam.attendance?.present ?? '—'}</td>)}
            <td className="text-center font-bold">{latestExam?.attendance?.present ?? '—'}</td>
          </tr>
          <tr>
            <td className="text-left italic font-bold">Working Days Upto</td>
            {attendanceSlots.map(slot => <td key={slot.key} className="text-center">{slot.exam.attendance?.total ?? '—'}</td>)}
            <td className="text-center font-bold">{latestExam?.attendance?.total ?? '—'}</td>
          </tr>
        </tbody>
      </table>
      </div>

      <div className="overflow-x-auto mt-3">
      <table className="w-full text-[11px] border-collapse border border-[#101b22]">
        <thead>
          <tr>
            <th rowSpan={2} className="border border-[#101b22] text-left px-2">Final Result</th>
            <th className="border border-[#101b22]">Max Marks</th>
            <th className="border border-[#101b22]">Marks Obt.</th>
            <th className="border border-[#101b22]">Percentage</th>
            <th className="border border-[#101b22]">Overall {resultLabel}</th>
            <th className="border border-[#101b22]">Rank</th>
            <th className="border border-[#101b22]" colSpan={2}>Exam Result</th>
          </tr>
          <tr className="font-bold">
            <td className="border border-[#101b22] text-center">{academicMaxTotal || '—'}</td>
            <td className="border border-[#101b22] text-center">{academicObtained}</td>
            <td className="border border-[#101b22] text-center">{overallPct != null ? `${overallPct}%` : '—'}</td>
            <td className="border border-[#101b22] text-center">{overallPct != null ? resultFor(overallPct, board) : '—'}</td>
            <td className="border border-[#101b22] text-center">{annualExam?.rank ? `${annualExam.rank}/${annualExam.class_size}` : (latestExam?.rank ? `${latestExam.rank}/${latestExam.class_size}` : '—')}</td>
            <td className="border border-[#101b22] text-center font-extrabold" colSpan={2}>{overallPct != null ? (passed ? 'PASSED' : 'FAILED') : '—'}</td>
          </tr>
        </thead>
      </table>
      </div>
      <p className="text-[10.5px] text-[#405058] mt-1">
        Date of Result Declaration: {fmtDate((annualExam || latestExam)?.exam_date)}
      </p>

      <p className="text-[11px] text-[#405058] mt-2">
        {isCbse
          ? 'Grades: A1 91–100 · A2 81–90 · B1 71–80 · B2 61–70 · C1 51–60 · C2 41–50 · D 33–40 · E below 33'
          : 'Result: Distinction 75% and above · First Division 60–74% · Second Division 45–59% · Third Division 33–44% · Fail below 33%'}
      </p>

      <div className="grid grid-cols-3 gap-y-8 mt-14 text-[11px] text-[#405058]">
        <div className="border-t border-[#101b22] pt-1 px-2">Class Teacher</div>
        <div className="border-t border-[#101b22] pt-1 px-2">Checker</div>
        <div className="border-t border-[#101b22] pt-1 px-2">Exam Incharge</div>
        <div className="border-t border-[#101b22] pt-1 px-2">Head of Institution</div>
        <div className="border-t border-[#101b22] pt-1 px-2">Parent's Signature</div>
        <div className="pt-1 px-2 text-right">Seal</div>
      </div>
    </PrintSheet>
  );
}
